import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, User, Mail, Shield, Check,
  Eye, EyeOff, RefreshCw, Loader2, AlertCircle,
} from "lucide-react";
import { AUTH_API, checkEmail, checkUsername, getPermissionsCatalog } from "../../../api/auth";

/* ================================================================
   CONSTANTS
================================================================ */
const COUNTRIES = [
  { value: "MA", label: "Maroc" },
  { value: "DZ", label: "Algérie" },
  { value: "TN", label: "Tunisie" },
  { value: "LY", label: "Libye" },
  { value: "EG", label: "Égypte" },
  { value: "FR", label: "France" },
  { value: "BE", label: "Belgique" },
  { value: "AE", label: "Émirats Arabes Unis" },
  { value: "SA", label: "Arabie Saoudite" },
  { value: "OTHER", label: "Autre" },
];

// Utilisé uniquement si le catalogue backend (permissions/catalog/) n'a pas
// pu être chargé — source de vérité normale = groups renvoyés par l'API,
// même pattern que TeamUsersList.jsx.
const FALLBACK_PERMISSION_GROUPS = [
  { key: "dashboard", label: "Dashboard", permissions: [
    { code: "dashboard.view", label: "View dashboard" },
    { code: "dashboard.view_own", label: "View own dashboard" },
  ]},
  { key: "orders", label: "Orders", permissions: [
    { code: "orders.view", label: "View orders" },
    { code: "orders.assign", label: "Assign orders" },
    { code: "orders.update_status", label: "Update status" },
    { code: "orders.view_stats", label: "View statistics" },
  ]},
  { key: "offers", label: "Offers", permissions: [
    { code: "offers.view", label: "View offers" },
    { code: "offers.view_marketplace", label: "View marketplace" },
    { code: "offers.create", label: "Create" },
    { code: "offers.update_own", label: "Update own" },
    { code: "offers.delete_own", label: "Delete own" },
    { code: "offers.change_status", label: "Change status" },
  ]},
  { key: "collaborations", label: "Collaborations", permissions: [
    { code: "collaborations.view", label: "View" },
    { code: "collaborations.view_own", label: "View own" },
    { code: "collaborations.create", label: "Create" },
    { code: "collaborations.respond", label: "Respond" },
    { code: "collaborations.manage", label: "Manage" },
  ]},
  { key: "wallets", label: "Wallets", permissions: [
    { code: "wallets.view_own", label: "View own wallet" },
    { code: "wallet_transfers.view", label: "View transfers" },
    { code: "wallet_transactions.view", label: "View transactions" },
  ]},
  { key: "commissions", label: "Commissions", permissions: [
    { code: "commissions.view_own", label: "View own" },
    { code: "commissions.view_stats", label: "View statistics" },
  ]},
  { key: "invoices", label: "Invoices", permissions: [
    { code: "invoices.view_own", label: "View own" },
    { code: "invoices.download", label: "Download" },
  ]},
  { key: "messaging", label: "Messaging", permissions: [
    { code: "messaging.view_own", label: "View conversations" },
    { code: "messaging.send", label: "Send messages" },
    { code: "messaging.mark_read", label: "Mark as read" },
  ]},
  { key: "profile", label: "Profile & Settings", permissions: [
    { code: "profile.update", label: "Update profile" },
    { code: "settings.update", label: "Update settings" },
  ]},
  { key: "team", label: "Team", permissions: [
    { code: "team.view", label: "View team" },
    { code: "team.create", label: "Create members" },
    { code: "team.update", label: "Update members" },
    { code: "team.manage_permissions", label: "Manage permissions" },
  ]},
];

/* ================================================================
   HELPERS
================================================================ */
function makePassword() {
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const digits = "0123456789";
  const all = lower + upper + digits;
  let p =
    lower[Math.floor(Math.random() * lower.length)] +
    upper[Math.floor(Math.random() * upper.length)] +
    digits[Math.floor(Math.random() * digits.length)] +
    digits[Math.floor(Math.random() * digits.length)];
  for (let i = 0; i < 8; i++) p += all[Math.floor(Math.random() * all.length)];
  return p.split("").sort(() => Math.random() - 0.5).join("");
}

function passwordStrength(value) {
  if (!value) return 0;
  let s = 0;
  if (value.length >= 8) s += 30;
  if (/[A-Z]/.test(value)) s += 20;
  if (/[a-z]/.test(value)) s += 20;
  if (/\d/.test(value)) s += 20;
  if (/[^A-Za-z0-9]/.test(value)) s += 10;
  return Math.min(s, 100);
}

function strengthLabel(s) {
  if (s < 40) return { text: "Weak", color: "bg-red-500" };
  if (s < 70) return { text: "Medium", color: "bg-yellow-500" };
  if (s < 90) return { text: "Good", color: "bg-blue-500" };
  return { text: "Strong", color: "bg-green-500" };
}

/* ================================================================
   SMALL COMPONENTS
================================================================ */
const inputCls =
  "w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors";

function FieldRow({ label, required, error, success, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-500 dark:text-slate-500 mb-1.5 block">
        {label}{required && <span className="text-red-400 dark:text-rose-400 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 dark:text-rose-400 mt-1 flex items-center gap-1"><AlertCircle size={11}/>{error}</p>}
      {!error && success && <p className="text-xs text-green-600 dark:text-emerald-400 mt-1">{success}</p>}
    </div>
  );
}

/* ================================================================
   MAIN COMPONENT
================================================================ */
const TeamAddUser = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: "", last_name: "", username: "",
    email: "", password: "",
    phone_number: "", country: "MA", city: "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [fullAccess, setFullAccess] = useState(true);
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);

  useEffect(() => {
    getPermissionsCatalog()
      .then((res) => setPermissionGroups(res.data?.groups || []))
      .catch(() => setPermissionGroups([]));
  }, []);

  const permGroups = permissionGroups.length ? permissionGroups : FALLBACK_PERMISSION_GROUPS;

  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldSuccess, setFieldSuccess] = useState({});
  const [globalError, setGlobalError] = useState(null);
  const [checking, setChecking] = useState({ email: false, username: false });
  const [submitting, setSubmitting] = useState(false);

  const emailDebounce = useRef(null);
  const usernameDebounce = useRef(null);
  const submitLock = useRef(false);   // prevent double-click race condition

  /* ── helpers ──────────────────────────────────────────────── */
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const setErr = (key, msg) =>
    setFieldErrors((p) => ({ ...p, [key]: msg }));
  const setOk = (key, msg) => {
    setFieldErrors((p) => ({ ...p, [key]: "" }));
    setFieldSuccess((p) => ({ ...p, [key]: msg }));
  };
  const clearSuccess = (key) =>
    setFieldSuccess((p) => ({ ...p, [key]: "" }));

  /* ── live email check ─────────────────────────────────────── */
  const handleEmailChange = (e) => {
    const value = e.target.value;
    set("email", value);
    setErr("email", "");
    clearSuccess("email");

    if (emailDebounce.current) clearTimeout(emailDebounce.current);
    if (!value.trim()) return;

    emailDebounce.current = setTimeout(async () => {
      try {
        setChecking((c) => ({ ...c, email: true }));
        const res = await checkEmail({ email: value });
        if (res.data?.exists) {
          setErr("email", "Email already registered.");
        } else {
          setOk("email", "Available ✓");
        }
      } catch {
        // ignore network errors during check
      } finally {
        setChecking((c) => ({ ...c, email: false }));
      }
    }, 500);
  };

  /* ── live username check ──────────────────────────────────── */
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    set("username", value);
    setErr("username", "");
    clearSuccess("username");

    if (usernameDebounce.current) clearTimeout(usernameDebounce.current);
    if (!value.trim()) return;

    usernameDebounce.current = setTimeout(async () => {
      try {
        setChecking((c) => ({ ...c, username: true }));
        const res = await checkUsername({ username: value });
        if (res.data?.exists) {
          setErr("username", "Username already taken.");
        } else {
          setOk("username", "Available ✓");
        }
      } catch {
        // ignore
      } finally {
        setChecking((c) => ({ ...c, username: false }));
      }
    }, 500);
  };

  const togglePerm = (code) =>
    setSelectedPerms((prev) =>
      prev.includes(code) ? prev.filter((p) => p !== code) : [...prev, code]
    );

  const handleGenPassword = () => {
    set("password", makePassword());
    setShowPwd(true);
  };

  /* ── submit ───────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // ref guard: block if already running (protects against double-click race)
    if (submitLock.current) return;
    submitLock.current = true;

    setGlobalError(null);

    // client-side guard
    if (checking.email || checking.username) {
      submitLock.current = false;
      return;
    }
    if (fieldErrors.email || fieldErrors.username) {
      submitLock.current = false;
      return;
    }

    try {
      setSubmitting(true);
      await AUTH_API.post("user/children/create/", {
        ...form,
        permissions: fullAccess ? ["*"] : selectedPerms,
      });
      navigate("/users/list");
    } catch (err) {
      const data = err.response?.data;

      // 403 — backend sends {"error": "..."} for permission/status failures
      if (data?.error) {
        setGlobalError(data.error);
        return;
      }

      // DRF field validation errors  {field: ["msg"]}
      if (data && typeof data === "object" && !Array.isArray(data)) {
        const newErrors = {};
        let hasFieldErrors = false;
        Object.entries(data).forEach(([k, v]) => {
          const msg = Array.isArray(v) ? v[0] : v;
          if (k === "non_field_errors") {
            setGlobalError(msg);
          } else {
            newErrors[k] = msg;
            hasFieldErrors = true;
          }
        });
        if (hasFieldErrors) setFieldErrors((p) => ({ ...p, ...newErrors }));
        return;
      }

      setGlobalError("Failed to create account. Please try again.");
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  const pwdStrength = passwordStrength(form.password);
  const { text: pwdLabel, color: pwdColor } = strengthLabel(pwdStrength);
  const isChecking = checking.email || checking.username;

  /* ================================================================
     RENDER
  ================================================================ */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/users/list"
          className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
        >
          <ArrowLeft size={18} />
        </Link>
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Add Team Member</h1>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-0.5">Create a sub-account for an employee</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Left: identity + credentials ───────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Personal info */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-6 space-y-4">
            <h2 className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2 text-sm">
              <User size={15} className="text-indigo-600 dark:text-indigo-400" />
              Personal Information
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <FieldRow label="First Name" error={fieldErrors.first_name}>
                <input
                  value={form.first_name}
                  onChange={(e) => set("first_name", e.target.value)}
                  placeholder="Ahmed"
                  className={inputCls}
                />
              </FieldRow>
              <FieldRow label="Last Name" error={fieldErrors.last_name}>
                <input
                  value={form.last_name}
                  onChange={(e) => set("last_name", e.target.value)}
                  placeholder="Benali"
                  className={inputCls}
                />
              </FieldRow>
            </div>

            <FieldRow label="Username" required error={fieldErrors.username} success={fieldSuccess.username}>
              <div className="relative">
                <input
                  required
                  value={form.username}
                  onChange={handleUsernameChange}
                  placeholder="ahmed.benali"
                  className={`${inputCls} pr-8 ${fieldErrors.username ? "border-red-400 dark:border-rose-500/50 focus:ring-red-400 dark:focus:ring-rose-500/50" : fieldSuccess.username ? "border-green-400 dark:border-emerald-500/50 focus:ring-green-400 dark:focus:ring-emerald-500/50" : ""}`}
                />
                {checking.username && (
                  <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400 dark:text-slate-500" />
                )}
              </div>
            </FieldRow>

            <FieldRow label="Phone Number" error={fieldErrors.phone_number}>
              <input
                value={form.phone_number}
                onChange={(e) => set("phone_number", e.target.value)}
                placeholder="+212600000000"
                className={inputCls}
              />
            </FieldRow>

            <div className="grid grid-cols-2 gap-4">
              <FieldRow label="Country" required error={fieldErrors.country}>
                <select
                  required
                  value={form.country}
                  onChange={(e) => set("country", e.target.value)}
                  className={inputCls}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </FieldRow>
              <FieldRow label="City" error={fieldErrors.city}>
                <input
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  placeholder="Casablanca"
                  className={inputCls}
                />
              </FieldRow>
            </div>
          </div>

          {/* Credentials */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-6 space-y-4">
            <h2 className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2 text-sm">
              <Mail size={15} className="text-indigo-600 dark:text-indigo-400" />
              Login Credentials
            </h2>

            <FieldRow label="Email" required error={fieldErrors.email} success={fieldSuccess.email}>
              <div className="relative">
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={handleEmailChange}
                  placeholder="ahmed@yourstore.com"
                  className={`${inputCls} pr-8 ${fieldErrors.email ? "border-red-400 dark:border-rose-500/50 focus:ring-red-400 dark:focus:ring-rose-500/50" : fieldSuccess.email ? "border-green-400 dark:border-emerald-500/50 focus:ring-green-400 dark:focus:ring-emerald-500/50" : ""}`}
                />
                {checking.email && (
                  <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400 dark:text-slate-500" />
                )}
              </div>
            </FieldRow>

            {/* Password */}
            <FieldRow label="Password" required error={fieldErrors.password}>
              <div className="space-y-2">
                <div className="flex items-center justify-end mb-1">
                  <button
                    type="button"
                    onClick={handleGenPassword}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 font-medium"
                  >
                    <RefreshCw size={11} />
                    Generate
                  </button>
                </div>
                <div className="relative">
                  <input
                    required
                    type={showPwd ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    placeholder="Min. 8 chars + 1 digit"
                    className={`${inputCls} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>

                {/* Strength bar */}
                {form.password && (
                  <div className="space-y-1">
                    <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${pwdColor}`}
                        style={{ width: `${pwdStrength}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-400 dark:text-slate-500">{pwdLabel}</p>
                  </div>
                )}

                {/* Copy hint */}
                {form.password && (
                  <div className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">
                      {showPwd ? form.password : "••••••••••••"}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(form.password)}
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium ml-3 shrink-0"
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
            </FieldRow>
          </div>
        </div>

        {/* ── Right: permissions + submit ─────────────────────────── */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-6 space-y-4">
            <h2 className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2 text-sm">
              <Shield size={15} className="text-indigo-600 dark:text-indigo-400" />
              Permissions
            </h2>

            {/* Full access toggle */}
            <div
              className={`flex items-center justify-between p-4 rounded-xl cursor-pointer border transition-colors ${
                fullAccess ? "bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/30" : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
              }`}
              onClick={() => setFullAccess((v) => !v)}
            >
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Full Access</p>
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">Same permissions as you</p>
              </div>
              <div
                className={`w-11 h-6 rounded-full transition-colors ${
                  fullAccess ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-700"
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow mt-0.5 transition-transform ${
                    fullAccess ? "translate-x-5 ml-0.5" : "ml-0.5"
                  }`}
                />
              </div>
            </div>

            {/* Per-permission checkboxes */}
            {!fullAccess && (
              <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
                {permGroups.map((group) => (
                  <div key={group.key}>
                    <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5">
                      {group.label}
                    </p>
                    <div className="space-y-1">
                      {group.permissions.map((perm) => {
                        const checked = selectedPerms.includes(perm.code);
                        return (
                          <label
                            key={perm.code}
                            className={`flex items-center gap-2.5 p-2 rounded-lg cursor-pointer text-sm transition-colors ${
                              checked
                                ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400"
                                : "hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={checked}
                              onChange={() => togglePerm(perm.code)}
                            />
                            <div
                              className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${
                                checked ? "bg-indigo-600" : "border-2 border-slate-300 dark:border-slate-600"
                              }`}
                            >
                              {checked && (
                                <Check size={10} className="text-white" strokeWidth={3} />
                              )}
                            </div>
                            {perm.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Global error */}
          {globalError && (
            <div className="flex items-start gap-2.5 p-3.5 bg-red-50 dark:bg-rose-500/10 border border-red-200 dark:border-rose-500/30 rounded-xl text-sm text-red-600 dark:text-rose-400">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              {globalError}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || isChecking || !!fieldErrors.email || !!fieldErrors.username}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting || isChecking ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <User size={16} />
            )}
            {submitting ? "Creating…" : isChecking ? "Checking…" : "Create Account"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamAddUser;
